package com.sddamico.components.widgets

import androidx.compose.runtime.Composable
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.models.TeamStandingResponse
import com.sddamico.util.formatLastUpdated
import com.sddamico.util.formatPoints
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@Composable
fun StandingsPanel(standings: List<TeamStandingResponse>, lastSyncedAt: String? = null) {
    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(1.25.cssRem)
            .fillMaxWidth()
    ) {
        // Header
        Column(Modifier.fillMaxWidth().padding(bottom = 1.cssRem)) {
            SpanText(
                "Overall Standings",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            if (lastSyncedAt != null) {
                SpanText(
                    "Updated ${formatLastUpdated(lastSyncedAt)}",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .color(SitePalette.textMuted)
                        .margin(top = 0.25.cssRem)
                )
            }
        }

        // Team rows
        standings.forEachIndexed { index, team ->
            StandingRow(index + 1, team)
            if (index < standings.lastIndex) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(SitePalette.border)
                        .margin(topBottom = 0.5.cssRem)
                        .toAttrs()
                )
            }
        }
    }
}

@Composable
private fun StandingRow(rank: Int, team: TeamStandingResponse) {
    Row(
        Modifier.fillMaxWidth().padding(topBottom = 0.4.cssRem),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Rank
        SpanText(
            "$rank",
            Modifier
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(
                    when (rank) {
                        1 -> SitePalette.accent
                        2 -> SitePalette.textSecondary
                        3 -> SitePalette.accentMuted
                        else -> SitePalette.textMuted
                    }
                )
                .minWidth(2.cssRem)
                .styleModifier { property("font-variant-numeric", "tabular-nums") }
        )

        // Team name and sport ranks
        Column(Modifier.margin(left = 0.5.cssRem)) {
            Link(
                "/clubhouse?clubId=${team.clubId}",
                team.clubName,
                Modifier.fontSize(0.9375.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            )
            SpanText(
                "Golf: #${team.golfRank} \u00B7 Men's: #${team.mensTennisRank} \u00B7 Women's: #${team.womensTennisRank}",
                Modifier.fontSize(0.6875.cssRem).color(SitePalette.textMuted)
            )
        }

        Spacer()

        // Score
        SpanText(
            formatPoints(team.overallScore),
            Modifier
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.accent)
                .styleModifier { property("font-variant-numeric", "tabular-nums") }
        )
    }
}
